/**
 * Trigger UI helpers, rule-engine types, and trigger translation.
 */
import { keywordToRegexPattern, TriggerType, } from '@polybank/shared';
// ─── Rule Engine Types ──────────────────────────────────────────────────────
// Extracted from @usex/rule-engine to avoid importing the full library in the browser
export var Operators;
(function (Operators) {
    Operators["Equals"] = "equals";
    Operators["NotEquals"] = "not-equals";
    Operators["GreaterThan"] = "greater-than";
    Operators["GreaterThanOrEquals"] = "greater-than-or-equals";
    Operators["LessThan"] = "less-than";
    Operators["LessThanOrEquals"] = "less-than-or-equals";
    Operators["StartsWith"] = "starts-with";
    Operators["EndsWith"] = "ends-with";
    Operators["Contains"] = "contains";
    Operators["NotContains"] = "not-matches";
    Operators["Matches"] = "matches";
    Operators["In"] = "in";
    Operators["NotIn"] = "not-in";
    Operators["DateAfter"] = "date-after";
    Operators["DateBefore"] = "date-before";
    Operators["DateAfterOrEquals"] = "date-after-or-equals";
    Operators["DateBeforeOrEquals"] = "date-before-or-equals";
    Operators["DateEquals"] = "date-equals";
})(Operators || (Operators = {}));
// ─── Trigger UI Helpers & Rule-Engine Translation ────────────────────────────
let _triggerTypeLabels;
function triggerTypeLabels() {
    if (!_triggerTypeLabels) {
        _triggerTypeLabels = {
            [TriggerType.AMOUNT_LESS]: 'Amount less than',
            [TriggerType.AMOUNT_LESS_EQUAL]: 'Amount less than or equal to',
            [TriggerType.AMOUNT_GREATER]: 'Amount greater than',
            [TriggerType.AMOUNT_GREATER_EQUAL]: 'Amount greater than or equal to',
            [TriggerType.AMOUNT_EQUAL]: 'Amount equal to',
            [TriggerType.DESCRIPTION_STARTS]: 'Description starts with',
            [TriggerType.DESCRIPTION_ENDS]: 'Description ends with',
            [TriggerType.DESCRIPTION_CONTAINS]: 'Description contains',
            [TriggerType.DESCRIPTION_NOT_CONTAINS]: 'Description does not contain',
            [TriggerType.ACCOUNT_IS]: 'Account is',
            [TriggerType.CURRENCY_IS]: 'Currency is',
            [TriggerType.DATE_STARTING_FROM]: 'Date starting from',
            [TriggerType.DATE_UNTIL]: 'Date until',
            [TriggerType.DATE_BETWEEN]: 'Date between',
        };
    }
    return _triggerTypeLabels;
}
export function getTriggerTypeLabel(triggerType) {
    return triggerTypeLabels()[triggerType];
}
export function formatTriggerText(trigger) {
    return ` ${triggerTypeLabels()[trigger.triggerType]} "${trigger.triggerValue}"`;
}
export function translateTrigger(trigger) {
    const { triggerType, triggerValue } = trigger;
    switch (triggerType) {
        case TriggerType.CURRENCY_IS:
            return {
                field: 'currency',
                operator: Operators.Equals,
                value: triggerValue,
            };
        case TriggerType.AMOUNT_LESS:
            return {
                field: 'absoluteAmount',
                operator: Operators.LessThan,
                value: Math.abs(parseFloat(triggerValue)),
            };
        case TriggerType.AMOUNT_LESS_EQUAL:
            return {
                field: 'absoluteAmount',
                operator: Operators.LessThanOrEquals,
                value: Math.abs(parseFloat(triggerValue)),
            };
        case TriggerType.AMOUNT_GREATER:
            return {
                field: 'absoluteAmount',
                operator: Operators.GreaterThan,
                value: Math.abs(parseFloat(triggerValue)),
            };
        case TriggerType.AMOUNT_GREATER_EQUAL:
            return {
                field: 'absoluteAmount',
                operator: Operators.GreaterThanOrEquals,
                value: Math.abs(parseFloat(triggerValue)),
            };
        case TriggerType.AMOUNT_EQUAL:
            return {
                field: 'absoluteAmount',
                operator: Operators.Equals,
                value: Math.abs(parseFloat(triggerValue)),
            };
        case TriggerType.DESCRIPTION_STARTS:
            return {
                field: 'description',
                operator: Operators.StartsWith,
                value: `${triggerValue.toLowerCase()}`,
            };
        case TriggerType.DESCRIPTION_ENDS:
            return {
                field: 'description',
                operator: Operators.EndsWith,
                value: `${triggerValue.toLowerCase()}`,
            };
        case TriggerType.DESCRIPTION_CONTAINS:
            return {
                field: 'description',
                operator: Operators.Matches,
                value: keywordToRegexPattern(triggerValue, 'smart'),
            };
        case TriggerType.DESCRIPTION_NOT_CONTAINS:
            return {
                field: 'description',
                operator: Operators.NotContains,
                value: '.*' +
                    triggerValue
                        .toLowerCase()
                        .replace(/([.*+?^=!:${}()|[\]/\\])/g, '\\$1') +
                    '.*',
            };
        case TriggerType.ACCOUNT_IS:
            return {
                field: 'bank_account_id',
                operator: Operators.Equals,
                value: triggerValue,
            };
        case TriggerType.DATE_STARTING_FROM:
            return {
                field: 'transaction_date',
                operator: Operators.DateAfterOrEquals,
                value: triggerValue,
            };
        case TriggerType.DATE_UNTIL:
            return {
                field: 'transaction_date',
                operator: Operators.DateBeforeOrEquals,
                value: triggerValue,
            };
        case TriggerType.DATE_BETWEEN: {
            const [from, to] = triggerValue.split(',');
            return [
                {
                    field: 'transaction_date',
                    operator: Operators.DateAfterOrEquals,
                    value: from,
                },
                {
                    field: 'transaction_date',
                    operator: Operators.DateBeforeOrEquals,
                    value: to,
                },
            ];
        }
        default:
            return undefined;
    }
}
